import { ChatBubbleLeftIcon } from "@heroicons/react/24/outline";
import { formatDateNoTime, formatPhoneNumber } from "@sp-crm/core";
import { SendSmsPanel } from "components/telecom/send-sms-panel";
import { SmsTranscriptPanel } from "components/telecom/sms-transcript-panel";
import { ConversationFeedBasicsFragment, SmsMessageStatus } from "generated/graphql";
import React, { useCallback, useState } from "react";
import { useCurrentUserId } from "store/selectors/hooks";
import { ContactSearchHitsLink } from "./contact-search-hits-link";
import {
    DeliveredStatusMessage,
    NotDeliveredStatusMessage,
    ReceivedStatusMessage,
    SendingStatusMessage,
    SentStatusMessage,
} from "./status-message";

interface SmsMessageThreadProps {
    thread: ConversationFeedBasicsFragment["threads"][number];
    initialThreadId?: string | null;
}

interface SmsStatusMessageProps {
    message: ConversationFeedBasicsFragment["threads"][number]["smsThread"]["messages"][number];
    mode: "icon" | "icon-and-text";
}
const SmsStatusMessage: React.FC<SmsStatusMessageProps> = props => {
    const { message, mode } = props;

    switch (message.status) {
        case SmsMessageStatus.Delivered:
            return <DeliveredStatusMessage mode={mode} />;
        case SmsMessageStatus.Failed:
            return <NotDeliveredStatusMessage mode={mode} />;
        case SmsMessageStatus.Sent:
            return <SentStatusMessage mode={mode} />;
        case SmsMessageStatus.Received:
            return <ReceivedStatusMessage mode={mode} />;
        case SmsMessageStatus.Undelivered:
            return <NotDeliveredStatusMessage mode={mode} />;
        case SmsMessageStatus.Accepted:
            return <SendingStatusMessage mode={mode} />;
        case SmsMessageStatus.Queued:
            return <SendingStatusMessage mode={mode} />;
        case SmsMessageStatus.Scheduled:
            return <SendingStatusMessage mode={mode} />;
        case SmsMessageStatus.Unknown:
            return null;
        default: {
            const exhaustiveCheck: never = message.status;
            console.error(`Unknown SMS message status: ${exhaustiveCheck}`);
            return null;
        }
    }
};

export const SmsMessageThread: React.FC<SmsMessageThreadProps> = props => {
    const { thread, initialThreadId } = props;
    const messages = thread.smsThread?.messages || [];
    const latestMessage = messages[0];
    const previousMessages = messages.slice(1);
    const isInbound = latestMessage.direction === "Inbound";
    const currentUserId = useCurrentUserId();
    const isOwnThread = thread.smsThread?.phoneLine?.userId === currentUserId;

    const [viewing, setViewing] = useState(initialThreadId === thread.id);

    const handleView = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setViewing(true);
    }, []);

    const handleDismiss = useCallback(() => {
        setViewing(false);
    }, []);

    const senderName = latestMessage.createdByUser?.displayName ?? "Unknown";

    return (
        <li key={thread.id}>
            <div
                className="flex items-center gap-3 px-3 py-2 bg-white border border-gray-200 rounded-sm hover:bg-gray-50 cursor-pointer"
                onClick={handleView}>
                <ChatBubbleLeftIcon
                    className={`h-4 w-4 text-gray-400 flex-shrink-0 ${
                        isInbound ? "transform scale-x-[-1]" : ""
                    }`}
                    aria-hidden="true"
                />
                <div className="flex-1 min-w-0 flex items-center gap-2">
                    <span className="font-medium text-sm text-gray-900 truncate">
                        {formatPhoneNumber(
                            isInbound ? latestMessage.from : latestMessage.to,
                        )}
                    </span>
                    <span className="text-xs text-gray-500">
                        {isOwnThread ? "You" : senderName}
                    </span>
                    <ContactSearchHitsLink hits={thread.contactSearchHits} />
                    <span className="text-gray-400 hidden sm:inline">&middot;</span>
                    <span className="text-sm text-gray-600 truncate hidden sm:inline flex-1">
                        {latestMessage.body}
                    </span>
                    {previousMessages.length > 0 && (
                        <span className="text-xs text-gray-400 hidden md:inline">
                            (+{previousMessages.length})
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-500 flex-shrink-0">
                    <span>{formatDateNoTime(latestMessage.dateAdded)}</span>
                    <SmsStatusMessage message={latestMessage} mode="icon" />
                </div>
            </div>
            {isOwnThread ? (
                <SendSmsPanel
                    isOpen={viewing}
                    onDismiss={handleDismiss}
                    recipient={isInbound ? latestMessage.from : latestMessage.to}
                />
            ) : thread.smsThread?.phoneLine ? (
                <SmsTranscriptPanel
                    isOpen={viewing}
                    onDismiss={handleDismiss}
                    phoneLineId={thread.smsThread.phoneLine.id}
                    phoneNumber={isInbound ? latestMessage.from : latestMessage.to}
                    senderName={senderName}
                />
            ) : null}
        </li>
    );
};
